import { Observable, Subscription } from 'rxjs';

export interface CollectionLockManager {
  request(
    name: string,
    options: {
      mode: 'shared' | 'exclusive';
      signal: AbortSignal;
    },
    callback: () => Promise<void>,
  ): Promise<void>;
}

export class CollectionWebLocksUnavailableError extends Error {
  constructor() {
    super('This browser cannot safely coordinate collection sessions.');
    this.name = 'CollectionWebLocksUnavailableError';
  }
}

const COLLECTION_SESSION_LOCK_PREFIX = 'mzic:collection-session';
type CollectionLockNotificationRunner = (notification: () => void) => void;

const runNotificationImmediately: CollectionLockNotificationRunner = (
  notification,
) => notification();

export class CollectionWebLock {
  constructor(
    private readonly lockManager: CollectionLockManager | null,
    private readonly runNotification: CollectionLockNotificationRunner = runNotificationImmediately,
  ) {}

  withShared<T>(
    collectionId: string,
    work: () => Observable<T>,
  ): Observable<T> {
    return this.withLock(collectionId, 'shared', work);
  }

  withExclusive<T>(
    collectionId: string,
    work: () => Observable<T>,
  ): Observable<T> {
    return this.withLock(collectionId, 'exclusive', work);
  }

  private withLock<T>(
    collectionId: string,
    mode: 'shared' | 'exclusive',
    work: () => Observable<T>,
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      if (!this.lockManager) {
        this.runNotification(() =>
          subscriber.error(new CollectionWebLocksUnavailableError()),
        );
        return undefined;
      }

      const abortController = new AbortController();
      let innerSubscription: Subscription | null = null;
      let releaseActiveLock: (() => void) | null = null;

      void this.lockManager
        .request(
          lockName(collectionId),
          { mode, signal: abortController.signal },
          async () => {
            if (subscriber.closed) return;

            await new Promise<void>((resolve) => {
              let released = false;
              const release = () => {
                if (released) return;
                released = true;
                releaseActiveLock = null;
                resolve();
              };
              releaseActiveLock = release;

              let inner: Observable<T>;
              try {
                inner = work();
              } catch (error) {
                this.runNotification(() => subscriber.error(error));
                release();
                return;
              }

              innerSubscription = inner.subscribe({
                next: (value) =>
                  this.runNotification(() => subscriber.next(value)),
                error: (error) => {
                  this.runNotification(() => subscriber.error(error));
                  release();
                },
                complete: () => {
                  this.runNotification(() => subscriber.complete());
                  release();
                },
              });

              if (subscriber.closed) {
                innerSubscription.unsubscribe();
                release();
              }
            });
          },
        )
        .catch((error: unknown) => {
          if (!subscriber.closed) {
            this.runNotification(() => subscriber.error(error));
          }
        });

      return () => {
        abortController.abort();
        innerSubscription?.unsubscribe();
        releaseActiveLock?.();
      };
    });
  }
}

function lockName(collectionId: string): string {
  const normalized = collectionId.trim();
  if (!normalized) {
    throw new TypeError('collectionId must be a non-empty string');
  }
  return `${COLLECTION_SESSION_LOCK_PREFIX}:${normalized}`;
}
